import React, { useContext, useMemo, memo } from "react";
import { Box, Grid } from "@mui/material";
import { TOContext } from "context/TContext";
import { useCreditCard } from "hooks/useCreditCard";
import TOCardPayment from "components/Payments/TOCardPayment";
import { usePaymentContext } from "context/TPaymentContext";
import PaymentDivider from "components/Shared/PaymentDivider";
import { useTipInfoSettingsContext } from "context/TipInfoSettingsContext";
import ApplePayButtonForward from "components/Payments/ApplePayButtonForward";
import GooglePayButtonForward from "components/Payments/GooglePayButtonForward";
import {
  PaymentMethodIcons,
  CashPaymentSection,
  PaymentHeaderSection,
  PaymentTypeHeader,
  useCommonVisibilityConditions,
  useCommonHandlers,
} from "./shared/PaymentSharedComponents";

// Memoized Apple Pay section component
const ApplePaySection = memo(
  ({ showApplePayButton, loaded, charges, userInfo, showApplePayDivider }) => {
    if (!showApplePayButton) return null;
    return (
      <>
        <Grid item xs={12}>
          <ApplePayButtonForward
            forwardSDKLoaded={loaded}
            handlePaymentRequest={() => {}}
            handleCardError={() => {}}
            purchaseModel={() => {}}
            applePayPaymentRequest={{
              total: {
                label: "Pos Order Payment",
                type: "final",
                amount:
                  Math.round(Number(charges?.balanceDue * 100 || 0)) / 100,
              },
              requireBillingContactFields: ["postalAddress"],
            }}
            userInfo={userInfo}
          />
        </Grid>
        {showApplePayDivider && (
          <Grid item xs={12}>
            <PaymentDivider />
          </Grid>
        )}
      </>
    );
  },
);

ApplePaySection.displayName = "ApplePaySection";

// Memoized Google Pay section component
const GooglePaySection = memo(
  ({ showGooglePayButton, userInfo, showGooglePayDivider }) => {
    if (!showGooglePayButton) return null;
    return (
      <>
        <Grid item xs={12}>
          <GooglePayButtonForward
            handlePaymentRequest={() => {}}
            handleCardError={() => {}}
            purchaseModel={() => {}}
            userInfo={userInfo}
          />
        </Grid>
        {showGooglePayDivider && (
          <Grid item xs={12}>
            <PaymentDivider />
          </Grid>
        )}
      </>
    );
  },
);

GooglePaySection.displayName = "GooglePaySection";

// Forward Payments component
const ForwardPayments = memo(({ loaded, error, bId, oId, isWebView }) => {
  const tipInfoSettings = useTipInfoSettingsContext();
  const { isDriver, charges, orderType, business } = useContext(TOContext);
  const {
    isForwardApplePaySupported,
    isForwardGooglePaySupported,
    isLoadingForWard,
  } = usePaymentContext();

  // Backend may send 0/1 or false/true. Any truthy value = "button hidden".
  const isApplePayDisabledForBusiness = Boolean(
    business?.settings?.disableApplePay,
  );
  const isGooglePayDisabledForBusiness = Boolean(
    business?.settings?.disableGooglePay,
  );

  const {
    userInfo,
    errorCard,
    setErrorCard,
    tokenForward,
    setHasTokenForward,
    handleSubmit,
    handleCashSubmit,
    loadingPay,
  } = useCreditCard();

  // Use shared common visibility conditions
  const commonVisibilityConditions = useCommonVisibilityConditions(
    loaded,
    error,
    isDriver,
    tipInfoSettings,
    orderType,
  );

  // Use shared common handlers
  const memoizedHandlers = useCommonHandlers(
    handleSubmit,
    handleCashSubmit,
    setErrorCard,
    setHasTokenForward,
  );

  // Memoize Forward-specific visibility conditions
  const visibilityConditions = useMemo(
    () => ({
      ...commonVisibilityConditions,
      showApplePayButton:
        isForwardApplePaySupported &&
        !isWebView &&
        !isApplePayDisabledForBusiness,

      showApplePayDivider:
        isForwardApplePaySupported &&
        !isLoadingForWard &&
        !isWebView &&
        !isApplePayDisabledForBusiness,

      showGooglePayButton:
        isForwardGooglePaySupported &&
        !isWebView &&
        !isGooglePayDisabledForBusiness,

      showGooglePayDivider:
        isForwardGooglePaySupported &&
        !isLoadingForWard &&
        !isWebView &&
        !isGooglePayDisabledForBusiness,
    }),
    [
      commonVisibilityConditions,
      isForwardApplePaySupported,
      isForwardGooglePaySupported,
      isWebView,
      isLoadingForWard,
      isApplePayDisabledForBusiness,
      isGooglePayDisabledForBusiness,
    ],
  );

  // Memoize credit card props for Forward payments
  const creditCardProps = useMemo(
    () => ({
      isWebView,
      userInfo,
      loadingPay,
      errorCard,
      setErrorCard: memoizedHandlers.setErrorCard,
      tokenForward,
      setHasTokenForward: memoizedHandlers.setHasTokenForward,
      handleSubmit: memoizedHandlers.handleSubmit,
      handleSubmitStripe: undefined, // Not used in Forward payments
      isStripeApplePaySupported: false,
      isStripeGooglePaySupported: false,
      paymentRequest: undefined,
    }),
    [
      isWebView,
      userInfo,
      loadingPay,
      errorCard,
      tokenForward,
      memoizedHandlers,
    ],
  );

  return (
    <>
      <PaymentHeaderSection
        isWebView={isWebView}
        bId={bId}
        oId={oId}
        showKitchenTips={visibilityConditions.showKitchenTips}
        showDriverTips={visibilityConditions.showDriverTips}
      />

      <Box sx={{ mt: isWebView ? 0 : 2 }}>
        <Grid container spacing={1}>
          <PaymentTypeHeader isWebView={isWebView} />

          <Grid item xs={12}>
            <PaymentMethodIcons />
          </Grid>

          <ApplePaySection
            showApplePayButton={visibilityConditions.showApplePayButton}
            loaded={loaded}
            charges={charges}
            userInfo={userInfo}
            showApplePayDivider={visibilityConditions.showApplePayDivider}
          />

          <GooglePaySection
            showGooglePayButton={visibilityConditions.showGooglePayButton}
            userInfo={userInfo}
            showGooglePayDivider={visibilityConditions.showGooglePayDivider}
          />

          <CashPaymentSection
            showDriverContent={visibilityConditions.showDriverContent}
            loadingPay={loadingPay}
            handleCashSubmit={memoizedHandlers.handleCashSubmit}
            oId={oId}
            showCashPayment={visibilityConditions.showCashPayment}
          />

          <Grid item xs={12}>
            {visibilityConditions.showCardPayment && (
              <TOCardPayment {...creditCardProps} />
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
});

ForwardPayments.displayName = "ForwardPayments";

export default ForwardPayments;
